import React, { useState, useEffect, useRef } from 'react';
import { ref, push, onValue, serverTimestamp, update } from 'firebase/database';
import { db } from '@react-native-firebase/database';
import { getLocalStorageItem } from 'core/helpers';
import StyleSheetRW from 'core/StyleSheetRW';
import { formatDate, formatDateLikeCalendar, formatDateWithLocalTimeZone } from 'src/utils/Date&TimeUtils';
import { Dimensions } from 'react-native';
import { MOBILE_VIEW_WIDTH } from 'res/constants';
import moment from 'moment';
import { fetchUserName } from 'src/utils/UserUtils';

interface ChatProps {
    selectedUser: { id: string; firstName: string };
    setShowLoader: (showLoader: boolean) => void;
}

interface Message {
    id: string;
    text: string;
    senderId: string;
    timestamp: number;
    senderName: string;
    receiverId: string;
    isDelivered: boolean;
    isRead: boolean;
}

const Chat: React.FC<ChatProps> = ({ selectedUser, setShowLoader }) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [newMessage, setNewMessage] = useState<string>('');
    const [currentUser, setCurrentUser] = useState<any>(null);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const screenWidth = Dimensions.get("window").width;

    useEffect(() => {
        const loggedInUser = getLocalStorageItem("loggedinUser");
        setCurrentUser(loggedInUser);
    }, []);

    useEffect(() => {
        if (!currentUser || !selectedUser) return;
        setShowLoader(true);
        const chatId = [currentUser.uid, selectedUser.id].sort().join('_');
        const messagesRef = ref(db, `messages/${chatId}`);

        const unsubscribe = onValue(messagesRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const loadedMessages: Message[] = [];
                Object.keys(data).forEach((date) => {
                    Object.keys(data[date]).forEach((messageId) => {
                        loadedMessages.push({
                            id: messageId,
                            ...data[date][messageId]
                        });
                    });
                });
                loadedMessages.sort((a, b) => a.timestamp - b.timestamp);
                setShowLoader(false);
                setMessages(loadedMessages);
            } else {
                setShowLoader(false);
                setMessages([]);
            }
        });


        return () => unsubscribe();
    }, [currentUser, selectedUser]);

    const handleSendMessage = async () => {
        if (newMessage.trim() === '' || !currentUser || !selectedUser) return;

        const chatId = [currentUser.uid, selectedUser.id].sort().join('_');
        const currentDate = formatDate(new Date(), "YYYY-MM-DD")
        const messagesRef = ref(db, `messages/${chatId}/${currentDate}`);
        const senderName = await fetchUserName()
         const newMessageRef = push(messagesRef, {
        text: newMessage,
        senderId: currentUser.uid,
        timestamp: serverTimestamp(),
        senderName: senderName,
        receiverId: selectedUser.id,
        isViewed: false,
        isDelivered: false,
        isRead: false
    });
        handleMessageDeliver(newMessageRef.key);
    setNewMessage('');
    };

    const handleMessageDeliver = (messageId: string) => {
        const chatId = [currentUser.uid, selectedUser.id].sort().join('_');
        const currentDate = formatDate(new Date(), "YYYY-MM-DD");
        const messageRef = ref(db, `messages/${chatId}/${currentDate}/${messageId}`);
        update(messageRef, { isDelivered: true });
    };

    const handleMessageRead = (messageId: string) => {
        const chatId = [currentUser.uid, selectedUser.id].sort().join('_');
        const currentDate = formatDate(new Date(), "YYYY-MM-DD");
        const messageRef = ref(db, `messages/${chatId}/${currentDate}/${messageId}`);
        update(messageRef, { isRead: true });
    };

    const scrollToBottom = () => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    };

    useEffect(() => {
        scrollToBottom()
    }, [messages, setMessages])
    useEffect(() => {
        if (!currentUser || !selectedUser) return;

        const unreadMessages = messages.filter(
            (message) => message.receiverId === currentUser.uid && !message.isRead
        );

        unreadMessages.forEach((message) => {
            handleMessageRead(message.id);
        });
    }, [messages, currentUser, selectedUser]);
    const formatChatDate = (timestamp: number) => {
        const now = moment();
        const messageDate = moment(timestamp);
    
        if (now.isSame(messageDate, 'day')) {
            return 'Today';
        } else if (now.subtract(1, 'days').isSame(messageDate, 'day')) {
            return 'Yesterday';
        } else if (now.subtract(1, 'days').isSame(messageDate, 'day')) {
            return messageDate.format('dddd'); 
        } else {
            return messageDate.format('D MMMM, YYYY'); 
        }
    };
    return (
        <div style={styles.container}>
            <h2 style={{ ...styles.heading, paddingLeft: screenWidth <= MOBILE_VIEW_WIDTH ? "20px" : undefined }}>Chat with {selectedUser.firstName}</h2>
            <div ref={chatContainerRef} style={{ ...styles.messagesContainer, overflowX: "hidden", overflowY: 'auto' }}>
                {messages.map((message, index) => {
                 const previousMessage = index > 0 ? messages[index - 1] : null;
                 const messageDate = formatChatDate(message.timestamp);
                 const previousMessageDate = previousMessage ? formatChatDate(previousMessage.timestamp) : null;
                 const messageTime = moment(message.timestamp).format('h:mm A');
                    
                    
                    // If it's a new date, render the date header
                    if (messageDate !== previousMessageDate) {
                        return (
                            <div key={message.id}>
                                   <div style={styles.date}>{messageDate}</div>
                                <div
                                    key={message.id}
                                    style={{
                                        ...styles.message,
                                        ...(message.senderId === currentUser?.uid ? styles.myMessage : styles.otherMessage),
                                    }}
                                >
                                    <div style={{ ...styles.messageContent, wordBreak: 'break-word' }}>
                                        <strong>{message.senderId === currentUser?.uid ? 'You' : selectedUser.firstName}</strong>: {message.text}
                                    </div>

                                        <div style={styles.detailts}>
                                     <div style={styles.timestamp}>{messageTime}</div>
                                        <div style={styles.tick}>
                                            {message.senderId === currentUser.uid && (
                                                <>
                                                    {!message.isDelivered && <span >✓</span>}
                                                    {message.isDelivered && !message.isRead && <span style={styles.parentGrayTick}><span style={styles.childGrayTick}>✓</span>✓</span>}
                                                    {message.isRead && <span style={styles.parentBlueTick}><span style={styles.childBlueTick}>✓</span>✓</span>}
                                                </>
                                            )}
                                        </div></div>
                                </div>
                            </div>
                        );
                    }

                    // Otherwise, just render the message
                    return (
                        <div
                            key={message.id}
                            style={{
                                ...styles.message,
                                ...(message.senderId === currentUser?.uid ? styles.myMessage : styles.otherMessage),
                            }}
                        >
                            <div style={{ ...styles.messageContent, wordBreak: 'break-word' }}>
                                <strong>{message.senderId === currentUser?.uid ? 'You' : selectedUser.firstName}</strong>: {message.text}
                                </div>

                             <div style={styles.detailts}>
                           <div style={styles.timestamp}>{messageTime}</div>
                                <div style={styles.tick}>
                                    {message.senderId === currentUser.uid && (
                                        <>
                                            {!message.isDelivered && <span>✓</span>}
                                            {message.isDelivered && !message.isRead && <span style={styles.parentGrayTick}><span style={styles.childGrayTick}>✓</span>✓</span>}
                                            {message.isRead &&<span style={styles.parentBlueTick}><span style={styles.childBlueTick}>✓</span>✓</span>}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div style={styles.inputContainer}>
                <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Type a message"
                    style={styles.input}
                />
                <button onClick={handleSendMessage} style={styles.button}>Send</button>
            </div>
        </div>
    );

};

const styles = StyleSheetRW.create(() => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: 20,
        fontFamily: "Outfit",
    },
    parentGrayTick:{ color: 'gray',position:"relative" },
    childGrayTick:{ color: 'gray',position:"absolute",top:4,left:1 },
    parentBlueTick:{ color: '#4FB6EC',position:"relative" },
    childBlueTick:{ color: '#4FB6EC',position:"absolute",top:4,left:1 },
    heading: { fontWeight: 'bold', fontSize: 32, color: '#2F4858', margin: 0 },
    messagesContainer: {
        flex: 1,
        marginBottom: 10,
    },
    timestamp: {
        fontSize: 12,
        color: 'gray',
       position:"absolute",
       bottom:4,
      right:17,
        
    },
    detailts:{
        position:"relative",
       minWidth:"70px",
      
          },
    
    message: {
        display: 'flex',
        margin: '5px 0',
    },
    tick: {
      
        fontSize: 12,
       bottom:5,
       position:"absolute",
       right:5
     

    },
    messageDetails:{
position:"relative",
width:"70px"
    },
    myMessage: {
        justifyContent: 'flex-end',
        marginLeft: 'auto',
        marginRight: 10,
        backgroundColor: '#2F4858',
        color: '#ffffff',
        border: '1px solid #ccc',
        borderRadius: 10,
        maxWidth: '60%',
        width: 'fit-content', 
        boxSizing: 'border-box',
        wordWrap: 'break-word',
    },
    otherMessage: {
        justifyContent: 'flex-start',
        marginLeft: 10,
        marginRight: 'auto',
        maxWidth: '60%',
        backgroundColor: '#f2f2f2',
        color: '#000000',
        borderRadius: 10,
        width: 'fit-content',
        boxSizing: 'border-box',
        wordWrap: 'break-word',
    },
    messageContent: {
        padding: 10,
        borderRadius: 10,
        position:'relative',
      
       
    },
    inputContainer: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: 10,
    },
    input: {
        flex: 1,
        padding: '10px',
        borderRadius: 10,
        border: '1px solid #ccc',
        marginRight: '10px',
        outline: 'none',
    },
    button: {
        padding: '10px 20px',
        borderRadius: 10,
        border: 'none',
        backgroundColor: '#2F4858',
        color: '#ffffff',
        cursor: 'pointer',
    },
    date: {
        textAlign: 'center',
        margin: '10px 0',
        fontWeight: 'bold',
    },
}));

export default Chat;